import React, { useEffect, useState } from 'react';
import { Row, Col } from 'reactstrap';
import WhatsappNotification from './notification/whatsapp';
import SmsNotification from './notification/sms';
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import cookieHelper from 'helpers/getCookieData';
import { encodePatientId } from 'helpers/url_crypto_helper';
import { get } from 'helpers/api_helper';
import { LEDGER_URL, PATIENT_URL } from 'helpers/url_helper';
import { isPaymentModuleEnabled, isWhatsappEnabled } from 'helpers/tenantModuleHelper';
import {
    RiPhoneLine,
    RiMailLine,
    RiMapPinLine,
    RiWhatsappLine,
    RiPrinterLine,
    RiAlertLine,
    RiAddCircleLine,
    RiMedicineBottleLine,
    RiFileTextLine,
    RiFolderUploadLine
} from "react-icons/ri";

const PatientProfile = ({ data = null, configData = null, callback = () => { } }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const clinic = cookieHelper.getCookie('_c') ? JSON.parse(atob(cookieHelper.getCookie('_c'))) : null;
    const [patientData, setPatientData] = useState(data);
    const [notificationModal, setNotificationModal] = useState(null);
    const [finances, setFinances] = useState({ total_billed: 0, total_paid: 0, net_balance: 0 });
    const [options, setOptions] = useState(() => {
        try {
            const cached = sessionStorage.getItem('options');
            return cached ? JSON.parse(cached) : { allergies: [], tags: [] };
        } catch (e) {
            return { allergies: [], tags: [] };
        }
    });

    useEffect(() => {
        setPatientData(data);
    }, [data]);

    // Fetch profile options (allergies, tags) for resolving titles
    useEffect(() => {
        get(`${PATIENT_URL}/profile/options`)
            .then(res => {
                if (res?.success && res?.body) {
                    sessionStorage.setItem('options', JSON.stringify(res.body));
                    setOptions(res.body);
                }
            })
            .catch(() => { });
    }, []);

    // Fetch live financial pulse
    useEffect(() => {
        if (!patientData?.id) return;
        get(`${LEDGER_URL}?patient_id=${patientData.id}&clinic_id=${clinic?.id || 8}`)
            .then(res => {
                if (res?.success && res?.body) {
                    setFinances({
                        total_billed: res.body.total_billed || 0,
                        total_paid: res.body.total_paid || 0,
                        net_balance: res.body.net_balance || 0
                    });
                }
            })
            .catch(() => { });
    }, [patientData?.id]);

    const handleTabOpen = (tab) => {
        history.push({
            pathname: '/patient',
            search: `?_p=${encodePatientId(patientData?.id)}&_t=${tab}`
        });
    };

    const parseAllergies = (str) => {
        if (!str) return [];
        try {
            const parsed = JSON.parse(str);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return str.split(',').map(s => s.trim()).filter(Boolean);
        }
    };

    const getAllergyTitle = (item) => {
        if (!item && item !== 0) return '';
        if (typeof item === 'object') return item.label || item.title || item.name || item.value || '';

        const found = (options?.allergies || []).find(o =>
            String(o.value) === String(item) || String(o.id) === String(item) || String(o.label).toLowerCase() === String(item).toLowerCase()
        );
        if (found) return found.label || found.title || found.name;

        return String(item);
    };

    const getTagTitle = (item) => {
        if (!item && item !== 0) return '';
        if (typeof item === 'object') return item.label || item.title || item.name || item.value || '';

        const found = (options?.tags || []).find(o =>
            String(o.value) === String(item) || String(o.id) === String(item) || String(o.label).toLowerCase() === String(item).toLowerCase()
        );
        if (found) return found.label || found.title || found.name;

        return String(item);
    };

    const allergiesList = parseAllergies(patientData?.allergies);
    const tagsList = parseAllergies(patientData?.patient_tags);

    const getInitials = (name) => {
        if (!name) return 'P';
        return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    };

    const fullName = `${patientData?.title || 'Mr.'} ${patientData?.first_name || ''} ${patientData?.last_name || ''}`.trim();

    return (
        <div className="patient-emr-header">
            <Row className="g-3 align-items-center">
                {/* Left: Patient Avatar & Demographics */}
                <Col lg={5} md={12}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="patient-avatar-box">
                            {patientData?.profile_pic ? (
                                <img
                                    src={`${process.env.REACT_APP_API_URL || 'http://localhost:4017/'}${patientData.profile_pic}`}
                                    alt={fullName}
                                />
                            ) : (
                                <span>{getInitials(fullName)}</span>
                            )}
                        </div>

                        <div>
                            <div className="patient-name-title">
                                <span>{fullName}</span>
                                <span className="patient-case-badge">
                                    {patientData?.case_no || `#${patientData?.id || ''}`}
                                </span>
                            </div>

                            <div className="patient-meta-items">
                                {patientData?.gender && (
                                    <span className="meta-item">
                                        <span className="badge bg-primary-subtle text-primary font-size-11">
                                            {t(patientData.gender) || patientData.gender}
                                        </span>
                                    </span>
                                )}
                                {patientData?.age > 0 && (
                                    <span className="meta-item font-weight-medium">
                                        {patientData.age} {t("Age") || "Years"}
                                    </span>
                                )}
                                {patientData?.mobile && (
                                    <span className="meta-item">
                                        <RiPhoneLine /> {patientData.mobile}
                                    </span>
                                )}
                                {patientData?.city && (
                                    <span className="meta-item">
                                        <RiMapPinLine /> {patientData.city}
                                    </span>
                                )}
                            </div>

                            {/* Communication Shortcuts */}
                            <div className="d-flex align-items-center gap-2 mt-2">
                                {patientData?.mobile && isWhatsappEnabled() && (
                                    <a
                                        href={`https://wa.me/91${patientData.mobile.replace(/\D/g, '')}?text=Hello%20${encodeURIComponent(fullName)},%20from%20Oralstop%20Dental%20Clinic.`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-sm btn-outline-success py-0.5 px-2 font-size-11 d-inline-flex align-items-center gap-1"
                                    >
                                        <RiWhatsappLine /> WhatsApp
                                    </a>
                                )}
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary py-0.5 px-2 font-size-11 d-inline-flex align-items-center gap-1"
                                    onClick={() => setNotificationModal(1)}
                                >
                                    <RiMailLine /> {t("Notifications") || "Notify"}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary py-0.5 px-2 font-size-11 d-inline-flex align-items-center gap-1"
                                    onClick={() => window.open(`/patinet/summary/${patientData?.id}`, '_blank')}
                                >
                                    <RiPrinterLine /> {t("Profile") || "EMR Summary"}
                                </button>
                            </div>
                        </div>
                    </div>
                </Col>

                {/* Center: Financial Pulse (Rendered ONLY when Billing & Accounts Module is Active) */}
                {isPaymentModuleEnabled() && (
                    <Col lg={4} md={6}>
                        <div className="financial-pulse-box">
                            <div className="pulse-item">
                                <div className="pulse-label">{t("Total Invoiced")}</div>
                                <div className="pulse-val total">₹{parseFloat(finances.total_billed || 0).toLocaleString('en-IN')}</div>
                            </div>
                            <div className="pulse-item border-start border-end px-2">
                                <div className="pulse-label">{t("Collections Received") || "Paid to Date"}</div>
                                <div className="pulse-val paid">₹{parseFloat(finances.total_paid || 0).toLocaleString('en-IN')}</div>
                            </div>
                            <div className="pulse-item">
                                <div className="pulse-label">{t("Outstanding Receivables") || "Outstanding Due"}</div>
                                <div className={`pulse-val ${finances.net_balance > 0 ? 'due' : 'total'}`}>
                                    ₹{Math.max(0, parseFloat(finances.net_balance || 0)).toLocaleString('en-IN')}
                                </div>
                            </div>
                        </div>
                    </Col>
                )}

                {/* Right: Quick Action Buttons */}
                <Col lg={isPaymentModuleEnabled() ? 3 : 7} md={6} className="d-flex justify-content-lg-end justify-content-start align-items-center mt-3 mt-lg-0">
                    <div className="d-flex flex-column gap-1.5 pt-1" style={{ width: '195px', gap: '5px' }}>
                        <button
                            type="button"
                            className="btn btn-sm btn-primary quick-action-btn shadow-sm"
                            onClick={() => handleTabOpen('Treatment')}
                        >
                            <RiAddCircleLine /> {t("Treatment Plan")}
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary quick-action-btn"
                            onClick={() => handleTabOpen('Prescription')}
                        >
                            <RiMedicineBottleLine /> {t("Prescriptions")}
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-info quick-action-btn"
                            onClick={() => handleTabOpen('Files')}
                        >
                            <RiFolderUploadLine /> {t("Files & X-Rays")}
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-success quick-action-btn"
                            onClick={() => handleTabOpen('Bills')}
                        >
                            <RiFileTextLine /> {t("Bills & Invoices")}
                        </button>
                    </div>
                </Col>
            </Row>

            {/* Medical Alerts & Allergies Banner (if any) */}
            {(allergiesList.length > 0 || tagsList.length > 0) && (
                <div className="medical-alerts-bar">
                    <RiAlertLine className="font-size-16" />
                    <span>{t("Allergies")}:</span>
                    <div className="d-flex align-items-center gap-1.5 flex-wrap">
                        {allergiesList.map((alg, i) => {
                            const title = getAllergyTitle(alg);
                            return (
                                <span key={`alg-${i}`} className="alert-tag" title={`${t("Allergy")}: ${title}`}>
                                    ⚠️ {t("Allergy")}: {title}
                                </span>
                            );
                        })}
                        {tagsList.map((tag, i) => {
                            const title = getTagTitle(tag);
                            return (
                                <span key={`tag-${i}`} className="badge bg-white text-dark border font-size-11" title={`Tag: ${title}`}>
                                    #{title}
                                </span>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Modals */}
            {notificationModal === 1 && (
                <WhatsappNotification
                    mobile={patientData?.mobile}
                    open={notificationModal === 1}
                    callback={() => setNotificationModal(null)}
                />
            )}
            {notificationModal === 2 && (
                <SmsNotification
                    mobile={patientData?.mobile}
                    open={notificationModal === 2}
                    callback={() => setNotificationModal(null)}
                />
            )}
        </div>
    );
};

export default PatientProfile;